import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Organization {
  id: string;
  name: string;
  slug: string;
  business_email?: string;
  business_phone?: string;
  business_address?: string;
  logo_url?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  settings?: any;
  is_active: boolean;
}

interface OrganizationMembership {
  organization_id: string;
  role: 'owner' | 'admin' | 'member';
  organization: Organization;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  memberships: OrganizationMembership[];
  switchOrganization: (orgId: string) => void;
  loading: boolean;
  refetchOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [memberships, setMemberships] = useState<OrganizationMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrganizations = async () => {
    if (!user) {
      setOrganizations([]);
      setMemberships([]);
      setCurrentOrganization(null);
      setLoading(false);
      return;
    }

    try {
      const { data: membershipData, error } = await supabase
        .from('organization_members' as any)
        .select('organization_id, role, organizations(*)')
        .eq('user_id', user.id);

      if (error) throw error;

      const orgs = membershipData?.map((m: any) => m.organizations).filter(Boolean) || [];
      const mems = membershipData?.map((m: any) => ({
        organization_id: m.organization_id,
        role: m.role as 'owner' | 'admin' | 'member',
        organization: m.organizations
      })) || [];

      setOrganizations(orgs);
      setMemberships(mems);

      const savedOrgId = localStorage.getItem('currentOrganizationId');
      const orgToSet = orgs.find(o => o.id === savedOrgId) || orgs[0] || null;
      setCurrentOrganization(orgToSet);

      if (orgToSet) {
        localStorage.setItem('currentOrganizationId', orgToSet.id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [user?.id]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCurrentOrganization(org);
      localStorage.setItem('currentOrganizationId', orgId);
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        memberships,
        switchOrganization,
        loading,
        refetchOrganizations: fetchOrganizations
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
